'use client'
import { PrismicRichText, PrismicImage } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";


/**
 * @typedef {import("@prismicio/client").Content.HeroSlice} HeroSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<HeroSlice>} HeroProps
 * @type {import("react").FC<HeroProps>}
 */
const Hero = ({ slice, index, slices }) => {
  const isLast = Array.isArray(slices) && index === slices.length - 1;
  const fallbackAlt = "Image de contenu";

  // Z-index de base : [40, 30, 20, 10] pour images [0, 1, 2, 3]
  const baseZIndexes = [40, 30, 20, 10];
  const [zIndexOrder, setZIndexOrder] = useState([40, 30, 20, 10]);

  // Animation brutaliste : rotation des z-index toutes les 3 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      setZIndexOrder(prev => {
        // Rotation des z-index : le plus bas passe en haut
        const newOrder = [...prev];
        const lowest = newOrder.pop(); // Retire le dernier (plus bas)
        newOrder.unshift(lowest); // L'ajoute au début (plus haut)
        return newOrder;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`bg-white ${isLast ? "md:pb-10" : ""}`}
    >
      {slice.variation === "default" && (
        <div className="w-full max-w-350 w-auto mx-auto px-4 md:px-10">
          <div className="flex flex-col justify-between min-h-screen md:min-h-auto md:h-[calc(100dvh-80px)]">
            <div className="h-full py-20 flex flex-col md:flex-row items-center relative">
              <div className="md:absolute flex w-full justify-between h-[270px] md:h-auto">
                <div className="">
                  <span className="text-[50vw] md:text-[320px] leading-thin font-normal font-neue-haas text-gray">M</span>
                  <h1 className="hidden md:block font-neue-haas text-[95px] font-normal text-black -mt-34 ml-5">Labo.</h1>
                </div>
                <span className="text-[50vw] md:text-[320px] leading-thin font-normal font-neue-haas text-gray">W.</span>
              </div>
              <div className="absolute left-0 top-0">

              </div>
              <div className="md:max-w-1/2 xl:max-w-3/5 relative md:m-auto">
                {isFilled.image(slice.primary.images[0]?.img) && (
                  <motion.div
                    className="relative w-full"
                    animate={{ zIndex: zIndexOrder[0] }}
                    transition={{ duration: 0.6, ease: "backOut" }}
                    style={{ zIndex: zIndexOrder[0] }}
                  >
                    <PrismicImage field={slice.primary.images[0].img} alt={slice.primary.images[0].img?.alt || fallbackAlt} className="w-full h-auto aspect-[16/9] object-cover rotate-6" />
                  </motion.div>
                )}
                {isFilled.image(slice.primary.images[1]?.img) && (
                  <motion.div
                    className="absolute top-0 w-full"
                    animate={{ zIndex: zIndexOrder[1] }}
                    transition={{ duration: 0.6, ease: "backOut" }}
                    style={{ zIndex: zIndexOrder[1] }}
                  >
                    <PrismicImage field={slice.primary.images[1].img} alt={slice.primary.images[1].img?.alt || fallbackAlt} className="w-full h-auto aspect-[16/9] object-cover -rotate-3" />
                  </motion.div>
                )}
                {isFilled.image(slice.primary.images[2]?.img) && (
                  <motion.div
                    className="absolute top-0 w-full"
                    animate={{ zIndex: zIndexOrder[2] }}
                    transition={{ duration: 0.6, ease: "backOut" }}
                    style={{ zIndex: zIndexOrder[2] }}
                  >
                    <PrismicImage field={slice.primary.images[2].img} alt={slice.primary.images[2].img?.alt || fallbackAlt} className="w-full h-auto aspect-[16/9] object-cover rotate-3" />
                  </motion.div>
                )}
                {isFilled.image(slice.primary.images[3]?.img) && (
                  <motion.div
                    className="absolute top-0 w-full"
                    animate={{ zIndex: zIndexOrder[3] }}
                    transition={{ duration: 0.6, ease: "backOut" }}
                    style={{ zIndex: zIndexOrder[3] }}
                  >
                    <PrismicImage field={slice.primary.images[3].img} alt={slice.primary.images[3].img?.alt || fallbackAlt} className="w-full h-auto aspect-[16/9] object-cover" />
                  </motion.div>
                )}
              </div>
              <h1 className="md:hidden font-neue-haas text-[95px] font-normal text-black text-center">Labo.</h1>
            </div>
            <div className="pb-5 text-center">
              {isFilled.richText(slice.primary.text) && (
                <PrismicRichText
                  field={slice.primary.text}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="font-neue-haas text-2xl font-extralight uppercase">{children}</p>
                    ),
                    hyperlink: ({ children, node }) => (
                      <a
                        href={node.data.url}
                        target={node.data.target}
                        className="underline hover:no-underline transition-all"
                      >
                        {children}
                      </a>
                    ),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {slice.variation === "contact" && (
        <div className="w-full max-w-350 w-auto mx-auto pl-4 md:pl-10 pt-20 md:pt-40 mb-20">
          <div className="flex flex-col md:flex-row gap-10">
            <div className="flex flex-col gap-5 flex-1 pr-4 md:pr-0">
              <div>
                {isFilled.richText(slice.primary.title) && (
                  <PrismicRichText
                    field={slice.primary.title}
                    components={{
                      heading1: ({ children }) => (
                        <h1 className="font-neue-haas text-6xl md:text-8xl font-medium uppercase">{children}<br />{children}<br />{children}</h1>
                      ),
                    }}
                  />
                )}
              </div>
              <div className="w-full h-[1px] bg-light-gray"></div>
              <div className="w-fit">
                {isFilled.richText(slice.primary.text_decoration) && (
                  <PrismicRichText
                    field={slice.primary.text_decoration}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="font-neue-haas text-lg font-extralight text-end">{children}</p>
                      ),
                    }}
                  />
                )}
              </div>
            </div>
            <div className="w-full md:pt-3 flex flex-col gap-1 pl-5 md:pl-0">
              {isFilled.image(slice.primary.img) && (
                <PrismicImage field={slice.primary.img} alt={slice.primary.img?.alt || fallbackAlt} className="object-cover h-75 h-120 xl:h-150 w-full" />
              )}
              {isFilled.richText(slice.primary.text) && (
                <PrismicRichText
                  field={slice.primary.text}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="font-neue-haas text-lg font-extralight">{children}</p>
                    ),
                    hyperlink: ({ children, node }) => (
                      <a
                        href={node.data.url}
                        target={node.data.target}
                        className="underline hover:no-underline transition-all"
                      >
                        {children}
                      </a>
                    ),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
